import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router";
import { Plus, Settings, AlertCircle } from "lucide-react";
import { Button } from "@web/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@web/components/ui/dropdown-menu";
import { LoadingOverlay } from "@web/components/ui/loading";
import { EmptyState } from "@web/components/ui/empty-state";
import { useWorkspaceStore, useLogStore, useStatsStore, useAuthStore } from "@web/stores";
import { LogSummary } from "@web/features/log/components/log-summary";
import { LogFilter } from "@web/features/log/components/log-filter";
import { LogList } from "@web/features/log/components/log-list";
import { LogFormDialog } from "@web/features/log/components/log-form-dialog";
import { LogDetailDialog } from "@web/features/log/components/log-detail-dialog";
import { DailyChart } from "@web/features/stats/components/daily-chart";
import { MethodChart } from "@web/features/stats/components/method-chart";
import { CategoryChart } from "@web/features/stats/components/category-chart";
import { UserStatsTable } from "@web/features/stats/components/user-stats-table";
import { CategoryManageDialog } from "@web/features/category/components/category-manage-dialog";
import { MethodManageDialog } from "@web/features/method/components/method-manage-dialog";
import {
  MemberRole,
  type LogWithRelations,
  type LogListQuery,
  type CreateLogDto,
  type UpdateLogDto,
} from "@repo/share-log-interfaces";
import { getMonthRange } from "@web/components/ui/date-range-picker-modal";
import { modalService } from "@web/features/modal";
import { toastService } from "@web/features/toast/toast-service";

export function WorkspaceDashboardPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const {
    currentWorkspace,
    fetchWorkspace,
    members,
    fetchMembers,
    currentWorkspaceStatus,
  } = useWorkspaceStore();
  const { logs, createLog, updateLog, deleteLog, fetchLogs, fetchLog, filter } =
    useLogStore();
  const {
    fetchStats,
    summary,
    dailyData,
    methodStats,
    categoryStats,
    userStats,
  } = useStatsStore();
  const { user } = useAuthStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const [logFormOpen, setLogFormOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<LogWithRelations | null>(null);
  const [detailLog, setDetailLog] = useState<LogWithRelations | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [methodDialogOpen, setMethodDialogOpen] = useState(false);

  const workspaceIdNum = workspaceId ? parseInt(workspaceId, 10) : 0;

  const isMaster = members.some(
    (m) => m.userId === user?.id && m.role === MemberRole.MASTER,
  );

  useEffect(() => {
    if (workspaceIdNum) {
      void fetchWorkspace(workspaceIdNum);
      void fetchMembers(workspaceIdNum);
      // fetchStats and fetchLogs are called by LogFilter's initial filter
    }
  }, [workspaceIdNum, fetchWorkspace, fetchMembers]);

  // 알림 클릭 시 logId 쿼리 파라미터로 상세 다이얼로그 자동 열기
  const pendingLogIdRef = useRef<string | null>(null);

  // logId 파라미터를 감지하여 ref에 저장하고 URL에서 즉시 제거
  useEffect(() => {
    const logId = searchParams.get("logId");
    if (!logId) return;

    pendingLogIdRef.current = logId;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("logId");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // 로그가 로드되면 pending logId로 상세 다이얼로그 열기
  useEffect(() => {
    const logId = pendingLogIdRef.current;
    if (!logId || logs.length === 0) return;

    pendingLogIdRef.current = null;
    const targetLog = logs.find((l) => l.id === Number(logId));
    if (targetLog) {
      setDetailLog(targetLog);
      setDetailDialogOpen(true);
    } else {
      // 현재 필터에 해당 로그가 없으면 단건 조회 후 해당 월로 필터 변경
      void (async () => {
        const log = await fetchLog(workspaceIdNum, Number(logId));
        if (!log) {
          toastService.warning("해당 기록을 찾을 수 없습니다.");
          return;
        }
        setDetailLog(log);
        setDetailDialogOpen(true);
        // 해당 로그의 월로 필터 변경
        const logDate = new Date(log.date);
        const monthRange = getMonthRange(logDate.getFullYear(), logDate.getMonth());
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("startDate", monthRange.startDate);
          next.set("endDate", monthRange.endDate);
          return next;
        }, { replace: true });
      })();
    }
  }, [logs]);

  const handleFilterChange = useCallback(
    (newFilter: LogListQuery) => {
      void fetchLogs(workspaceIdNum, newFilter);
      void fetchStats(workspaceIdNum, newFilter);
    },
    [workspaceIdNum, fetchLogs, fetchStats],
  );

  const handleLogFormSubmit = async (data: CreateLogDto | UpdateLogDto) => {
    if (editingLog) {
      await updateLog(workspaceIdNum, editingLog.id, data as UpdateLogDto);
    } else {
      await createLog(workspaceIdNum, data as CreateLogDto);
    }
    setEditingLog(null);
    void fetchStats(workspaceIdNum, filter);
  };

  const handleLogClick = (log: LogWithRelations) => {
    setDetailLog(log);
    setDetailDialogOpen(true);
  };

  const handleEditLog = (log: LogWithRelations) => {
    setEditingLog(log);
    setLogFormOpen(true);
  };

  const handleDeleteLog = async (logId: number) => {
    const confirmed = await modalService.destructive("정말 삭제하시겠습니까?");
    if (!confirmed) return;
    await deleteLog(workspaceIdNum, logId);
    setDetailDialogOpen(false);
    void fetchStats(workspaceIdNum, filter);
  };

  const handleOpenLogForm = () => {
    setEditingLog(null);
    setLogFormOpen(true);
  };

  if (currentWorkspaceStatus === "loading" && !currentWorkspace) {
    return <LoadingOverlay />;
  }

  if (!currentWorkspace) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="워크스페이스를 찾을 수 없습니다"
        description="요청하신 워크스페이스가 존재하지 않거나 접근 권한이 없습니다."
        className="min-h-[60vh]"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{currentWorkspace.name}</h1>
          <p className="text-muted-foreground">
            {currentWorkspace.memberCount}명의 멤버
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                설정
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setCategoryDialogOpen(true)}>
                카테고리 관리
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setMethodDialogOpen(true)}>
                결제 수단 관리
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filter */}
      <LogFilter
        workspaceId={workspaceIdNum}
        onFilterChange={handleFilterChange}
      />

      {/* Summary */}
      <LogSummary summary={summary} />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DailyChart data={dailyData} />
        <MethodChart data={methodStats} />
        <CategoryChart data={categoryStats} />
        <UserStatsTable data={userStats} />
      </div>

      {/* Log List */}
      <LogList
        workspaceId={workspaceIdNum}
        currentUserId={user?.id ?? 0}
        onClick={handleLogClick}
        onEdit={handleEditLog}
        onDelete={handleDeleteLog}
      />

      {/* Dialogs */}
      <LogFormDialog
        open={logFormOpen}
        onOpenChange={setLogFormOpen}
        workspaceId={workspaceIdNum}
        log={editingLog}
        onSubmit={handleLogFormSubmit}
      />

      <LogDetailDialog
        log={detailLog}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        isOwner={detailLog != null && detailLog.userId === user?.id}
        onEdit={handleEditLog}
        onDelete={handleDeleteLog}
      />

      <CategoryManageDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        workspaceId={workspaceIdNum}
        isMaster={isMaster}
      />

      <MethodManageDialog
        open={methodDialogOpen}
        onOpenChange={setMethodDialogOpen}
        workspaceId={workspaceIdNum}
        isMaster={isMaster}
      />

      {/* Floating Action Button */}
      <Button
        onClick={handleOpenLogForm}
        size="icon"
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50"
      >
        <Plus className="h-6 w-6" />
      </Button>
    </div>
  );
}

